import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/api/query-hooks";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  useAuth,
  useUser,
} from "@clerk/clerk-react";
import { lazy, Suspense, useEffect } from "react";
import type { ReactNode } from "react";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { OnboardingProvider, useOnboarding } from "./context/OnboardingContext";
import { FutureSubscriptionProvider } from "./context/FutureSubscriptionContext";
import { configureClerkToken } from "./lib/backend";
import { AnalyticsProvider } from "./lib/analytics";
import { identifyAnalyticsUser, resetAnalyticsUser } from "./lib/analytics-client";
import { setObservabilityUser } from "./lib/observability";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FeaturePageGate, type FeatureKey } from "./lib/entitlements";

import { CommandPalette } from "./components/CommandPalette";
import { AIChatModal } from "./components/AIChatModal";
import { NotificationPanel } from "./components/NotificationPanel";
import { AppLoader } from "@/components/ui/app-loader";
import { MobilePwaPrompt } from "./components/MobilePwaPrompt";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { ShortcutHelpModal } from "./components/ShortcutHelpModal";
import { StreakVisitRecorder } from "./components/StreakVisitRecorder";
import { Orizonte12Announcement } from "./components/Orizonte12Announcement";
import { SnapSolveWidget } from "./components/SnapSolveWidget";

const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const Intelligence = lazy(() => import("./pages/Intelligence"));
const Analytics = lazy(() => import("./pages/Analytics"));
const DecisionInbox = lazy(() => import("./pages/DecisionInbox"));
const Vision = lazy(() => import("./pages/Vision"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Scenarios = lazy(() => import("./pages/Scenarios"));
const Accounts = lazy(() => import("./pages/Accounts"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminReadiness = lazy(() => import("./pages/AdminReadiness"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const FutureStack = lazy(() => import("./pages/FutureStack"));
const Pricing = lazy(() => import("./pages/Pricing"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const PublicStack = lazy(() => import("./pages/PublicStack"));
const Teams = lazy(() => import("./pages/Teams"));
const DownloadMac = lazy(() => import("./pages/DownloadMac"));

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "";
const isClerkConfigured = Boolean(CLERK_PUBLISHABLE_KEY) && CLERK_PUBLISHABLE_KEY.startsWith("pk_");

const ClerkSetupError = () => (
  <div className="min-h-screen bg-orizonte-bg flex items-center justify-center px-4">
    <div className="max-w-md w-full text-center space-y-4">
      <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
        <span className="text-red-400 text-2xl">!</span>
      </div>
      <h1 className="text-lg font-semibold text-orizonte-text">Clerk is not configured</h1>
      <p className="text-sm text-orizonte-textMuted">
        Authentication requires a valid Clerk publishable key. Add{" "}
        <code className="px-1.5 py-0.5 rounded bg-white/5 text-orizonte-lime text-xs">
          VITE_CLERK_PUBLISHABLE_KEY
        </code>{" "}
        to your environment and restart the dev server.
      </p>
      <p className="text-xs text-orizonte-textMuted">
        Create a Clerk application at{" "}
        <a
          href="https://clerk.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-orizonte-lime hover:underline"
        >
          clerk.com
        </a>{" "}
        and copy your publishable key.
      </p>
    </div>
  </div>
);

type ProtectedRouteProps = {
  children: ReactNode;
  requireOnboarding?: boolean;
};

const WorkspaceLoader = () => (
  <div className="min-h-screen bg-orizonte-bg flex items-center justify-center">
    <AppLoader label="Loading your workspace..." />
  </div>
);

const PageSuspense = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<WorkspaceLoader />}>{children}</Suspense>
);

const ProtectedRoute = ({
  children,
  requireOnboarding = true,
}: ProtectedRouteProps) => {
  const { profile, loading: profileLoading } = useOnboarding();
  if (profileLoading) {
    return <WorkspaceLoader />;
  }
  if (requireOnboarding && !profile?.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!requireOnboarding && profile?.onboardingCompleted) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const AuthenticatedRoute = ({
  children,
  requireOnboarding = true,
}: ProtectedRouteProps) => (
  <>
    <SignedIn>
      <ProtectedRoute requireOnboarding={requireOnboarding}>
        <PageSuspense>{children}</PageSuspense>
      </ProtectedRoute>
    </SignedIn>
    <SignedOut>
      <Navigate to="/auth" replace />
    </SignedOut>
  </>
);

const protectedRoutes: Array<{ path: string; element: ReactNode; featureKey?: FeatureKey }> = [
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/subscriptions", element: <Subscriptions /> },
  { path: "/calendar", element: <Calendar /> },
  { path: "/intelligence", element: <Intelligence />, featureKey: "renewal_risk_scoring" },
  { path: "/decision-inbox", element: <DecisionInbox /> },
  { path: "/analytics", element: <Analytics />, featureKey: "advanced_spend_charts" },
  { path: "/scenarios", element: <Scenarios />, featureKey: "scenario_simulator" },
  { path: "/accounts", element: <Accounts /> },
  { path: "/teams", element: <Teams /> },
  { path: "/future-stack", element: <FutureStack />, featureKey: "future_stack_buy_decisions" },
  { path: "/vision", element: <Vision /> },
  { path: "/settings", element: <SettingsPage /> },
  { path: "/admin/readiness", element: <AdminReadiness /> },
];

const BackendAuthBridge = ({ children }: { children: ReactNode }) => {
  const { getToken } = useAuth();
  const { user } = useUser();

  configureClerkToken(() => getToken());

  useEffect(() => {
    configureClerkToken(() => getToken());
  }, [getToken]);

  useEffect(() => {
    if (!user) {
      resetAnalyticsUser();
      setObservabilityUser(null);
      return;
    }

    identifyAnalyticsUser(user.id, {
      signed_in: true,
      email_verified: user.primaryEmailAddress?.verification?.status === "verified",
    });
    setObservabilityUser({ id: user.id });
  }, [user]);

  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <ErrorBoundary>
      <SnapSolveWidget />
      <SignedIn>
        <StreakVisitRecorder />
        <Orizonte12Announcement />
        <CommandPalette />
        <AIChatModal />
        <NotificationPanel />
        <MobileBottomNav />
        <ShortcutHelpModal />
      </SignedIn>
      <MobilePwaPrompt />

      <Routes>
        <Route
          path="/"
          element={
            <PageSuspense>
              <Landing />
            </PageSuspense>
          }
        />
        <Route
          path="/auth/*"
          element={
            <PageSuspense>
              <Auth />
            </PageSuspense>
          }
        />
        <Route
          path="/terms"
          element={
            <PageSuspense>
              <Terms />
            </PageSuspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <PageSuspense>
              <Privacy />
            </PageSuspense>
          }
        />
        <Route
          path="/pricing"
          element={
            <PageSuspense>
              <Pricing />
            </PageSuspense>
          }
        />
        <Route
          path="/download"
          element={
            <PageSuspense>
              <DownloadMac />
            </PageSuspense>
          }
        />
        <Route
          path="/refund-policy"
          element={
            <PageSuspense>
              <RefundPolicy />
            </PageSuspense>
          }
        />
        <Route
          path="/u/:username/stack"
          element={
            <PageSuspense>
              <PublicStack />
            </PageSuspense>
          }
        />
        <Route
          path="/onboarding"
          element={
            <AuthenticatedRoute requireOnboarding={false}>
              <Onboarding />
            </AuthenticatedRoute>
          }
        />
        {protectedRoutes.map(({ path, element, featureKey }) => (
          <Route
            key={path}
            path={path}
            element={
              <AuthenticatedRoute>
                {featureKey ? <FeaturePageGate featureKey={featureKey}>{element}</FeaturePageGate> : element}
              </AuthenticatedRoute>
            }
          />
        ))}
        <Route
          path="*"
          element={
            <PageSuspense>
              <NotFound />
            </PageSuspense>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
};

const App = () => {
  if (!isClerkConfigured) {
    return <ClerkSetupError />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsProvider>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <TooltipProvider>
            <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
              <BrowserRouter
                future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
              >
                <BackendAuthBridge>
                  <OnboardingProvider>
                    <SubscriptionProvider>
                      <FutureSubscriptionProvider>
                        <Toaster />
                        <Sonner />
                        <AppRoutes />
                      </FutureSubscriptionProvider>
                    </SubscriptionProvider>
                  </OnboardingProvider>
                </BackendAuthBridge>
              </BrowserRouter>
            </ClerkProvider>
          </TooltipProvider>
        </ThemeProvider>
      </AnalyticsProvider>
    </QueryClientProvider>
  );
};

export default App;
